<script setup>
// ---------------------------------------------------------------------------
// How long cases take, by month.
//
// Thirteen columns: one per month up to a year, then everything longer. Past a
// year the exact month stops being the interesting part — what a reader wants is
// how many cases run long.
//
// Cases with no usable start date are not a column. They have no length, so a
// bar for them sits on a different axis to every other bar, and it was the
// second tallest thing in the picture. The note under the chart says they are
// out; that is the honest place for a base a chart cannot draw.
//
// Every user-facing string arrives as a prop from the Django template, so the
// translations stay in the .po files and this component holds none of them.
// ---------------------------------------------------------------------------

import { computed } from 'vue'
import { scaleBand, scaleLinear } from 'd3-scale'

import { summarise } from '../cases.js'
import { useCases } from '../useCases.js'

const props = defineProps({
    src: { type: String, required: true },
    alt: { type: String, default: '' },
    axisMonths: { type: String, default: '' },
    axisCount: { type: String, default: '' },
    labelOver: { type: String, default: '' },
    note: { type: String, default: '' },
    errorText: { type: String, default: '' }
})

// A fixed viewBox with a fluid width: the drawing scales with the column and
// the type inside it stays at one size, which is also what a screenshot needs.
const W = 960
const H = 400
const PAD = { top: 28, right: 20, bottom: 56, left: 56 }

const { rows, visible, failed } = useCases(props.src)

// `rows` only says whether the file arrived; `visible` is what the filter left.
const summary = computed(() => (rows.value.length ? summarise(visible.value) : null))

const columns = computed(() => [
    ...summary.value.months.map((count, index) => ({ key: index + 1, label: String(index + 1), count })),
    { key: 'over', label: props.labelOver, count: summary.value.overYear, aside: true }
])

const x = computed(() =>
    scaleBand()
        .domain(columns.value.map((column) => column.key))
        .range([PAD.left, W - PAD.right])
        .padding(0.25)
)

const y = computed(() =>
    scaleLinear()
        .domain([0, Math.max(...columns.value.map((column) => column.count), 1)])
        .nice()
        .range([H - PAD.bottom, PAD.top])
)

const bars = computed(() =>
    columns.value.map((column) => ({
        ...column,
        x: x.value(column.key),
        width: x.value.bandwidth(),
        y: y.value(column.count),
        height: y.value(0) - y.value(column.count)
    }))
)

const countTicks = computed(() => y.value.ticks(4).map((count) => ({ count, at: y.value(count) })))

const number = (value) => value.toLocaleString('cs-CZ')
</script>

<template>
    <p v-if="failed" class="chart__failed">{{ errorText }}</p>

    <figure v-else-if="summary" class="chart">
        <svg class="chart__plot" :viewBox="`0 0 ${W} ${H}`" role="img" :aria-label="alt">
            <g class="chart__grid">
                <line
                    v-for="tick in countTicks"
                    :key="tick.count"
                    :x1="PAD.left"
                    :x2="W - PAD.right"
                    :y1="tick.at"
                    :y2="tick.at"
                />
            </g>

            <g class="chart__bars">
                <rect
                    v-for="bar in bars"
                    :key="bar.key"
                    :class="{ 'chart__bar--aside': bar.aside }"
                    :x="bar.x"
                    :y="bar.y"
                    :width="bar.width"
                    :height="bar.height"
                />
            </g>

            <g class="chart__values">
                <text
                    v-for="bar in bars"
                    :key="bar.key"
                    :x="bar.x + bar.width / 2"
                    :y="bar.y - 7"
                    text-anchor="middle"
                >{{ number(bar.count) }}</text>
            </g>

            <g class="chart__axis">
                <line :x1="PAD.left" :x2="W - PAD.right" :y1="y(0)" :y2="y(0)" />

                <text
                    v-for="bar in bars"
                    :key="bar.key"
                    class="chart__tick"
                    :x="bar.x + bar.width / 2"
                    :y="H - PAD.bottom + 18"
                    text-anchor="middle"
                >{{ bar.label }}</text>

                <text
                    v-for="tick in countTicks"
                    :key="tick.count"
                    class="chart__tick"
                    :x="PAD.left - 10"
                    :y="tick.at + 4"
                    text-anchor="end"
                >{{ number(tick.count) }}</text>

                <text class="chart__axis-label" :x="W - PAD.right" :y="H - 12" text-anchor="end">
                    {{ axisMonths }}
                </text>
                <text class="chart__axis-label" x="0" :y="PAD.top - 12" text-anchor="start">
                    {{ axisCount }}
                </text>
            </g>
        </svg>

        <figcaption class="note">{{ note }}</figcaption>
    </figure>
</template>
